/**
 * Single sanitization chokepoint. Every render — preview pane, share-URL
 * landing, exported HTML, print stylesheet target — passes its markdown-it
 * output through this function before it reaches the DOM.
 *
 * Spec §13 is the source of truth for the allowlist. The pipeline philosophy
 * is fail-closed: if a tag or attribute isn't on the list, it's stripped.
 * If a feature later needs something new (e.g., MathML extensions for KaTeX,
 * extra ARIA attributes for accessibility) extend the allowlist here. Never
 * bypass sanitize for "trusted" markdown — share URLs make every document
 * an attacker-controlled string.
 *
 * KaTeX / Mermaid / Shiki extensions in Phase 4 will pre-render their HTML
 * into placeholder tokens before sanitize runs, then restore the rendered
 * fragments after — so their output is never double-sanitized. That escape
 * hatch lives in `index.ts`, not here.
 */

import DOMPurify, { type Config } from 'isomorphic-dompurify'

// Standard text + flow + sectioning content used by markdown-it core + plugins.
// Includes <details>/<summary> for collapsible blocks, <mark> for callouts +
// preview search, <aside> for callout containers (post callout plugin).
const ALLOWED_HTML_TAGS = [
  'a', 'abbr', 'address', 'article', 'aside', 'b', 'bdi', 'bdo', 'blockquote',
  'br', 'caption', 'cite', 'code', 'col', 'colgroup', 'data', 'dd', 'del',
  'details', 'dfn', 'div', 'dl', 'dt', 'em', 'figcaption', 'figure', 'footer',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hgroup', 'hr', 'i', 'img',
  'input', 'ins', 'kbd', 'li', 'main', 'mark', 'nav', 'ol', 'p', 'picture',
  'pre', 'q', 'rb', 'rp', 'rt', 'rtc', 'ruby', 's', 'samp', 'section', 'small',
  'source', 'span', 'strong', 'sub', 'summary', 'sup', 'table', 'tbody', 'td',
  'tfoot', 'th', 'thead', 'time', 'tr', 'u', 'ul', 'var', 'wbr',
]

// SVG family — Mermaid output lives entirely in here. Constrained to math/
// diagram contexts; <foreignObject> kept off the list to prevent HTML
// re-entry from inside SVG. SVG animation tags (animate, animateMotion,
// animateTransform, set) deliberately omitted — not part of V1 feature set
// and a known UI-redress / phishing vector (animations can be used to hide
// or shift UI off-screen).
const ALLOWED_SVG_TAGS = [
  'svg', 'g', 'defs', 'desc', 'metadata', 'title', 'symbol', 'use', 'marker',
  'mask', 'clipPath', 'pattern', 'linearGradient', 'radialGradient', 'stop',
  'circle', 'ellipse', 'line', 'path', 'polygon', 'polyline', 'rect',
  'text', 'textPath', 'tspan', 'image', 'switch', 'view', 'a',
  'filter', 'feBlend', 'feColorMatrix', 'feComposite', 'feFlood',
  'feGaussianBlur', 'feMerge', 'feMergeNode', 'feOffset', 'feTurbulence',
]

// MathML family — KaTeX falls back to MathML in some configs (and we may
// enable mathmlOnly later for screen-reader fidelity). Keeping the family
// allowed now means no surprises when that switch flips.
const ALLOWED_MATHML_TAGS = [
  'math', 'maction', 'annotation', 'annotation-xml', 'menclose', 'merror',
  'mfenced', 'mfrac', 'mi', 'mmultiscripts', 'mn', 'mo', 'mover', 'mpadded',
  'mphantom', 'mroot', 'mrow', 'ms', 'mspace', 'msqrt', 'mstyle', 'msub',
  'msup', 'msubsup', 'mtable', 'mtd', 'mtext', 'mtr', 'munder', 'munderover',
  'semantics',
]

const ALLOWED_ATTRS = [
  // Core text + link + image attributes.
  // `target`/`rel` deliberately omitted — markdown link output never needs
  // them; a post-processor can re-add `target="_blank" rel="noopener"` for
  // external anchors later if we want that UX. Allowing them now lets a
  // share-URL doc craft `<a target="_top" href="…">` which can break out
  // of embeds, plus `rel="canonical"`-style abuse.
  // `name` kept (used by anchor fragments + some markdown plugins).
  'href', 'src', 'srcset', 'sizes', 'alt', 'title', 'name',
  'type', 'value', 'checked', 'disabled', 'open',
  // Structural. `role` + `tabindex` removed — markdown content doesn't
  // need to assert ARIA roles or pull itself into the keyboard tab order,
  // and both are vectors for UI confusion (a `<div role="dialog">` from
  // a share-URL doc could spoof native browser UI in screen readers).
  'class', 'id', 'lang', 'dir',
  // Table layout
  'colspan', 'rowspan', 'align', 'valign', 'scope', 'headers',
  // `style` DELIBERATELY OMITTED — full-viewport overlay attack via
  // `<div style="position:fixed;inset:0;…">` was the original UI-redress
  // vector. Trusted KaTeX / Shiki / Mermaid output is re-inserted AFTER
  // sanitize (KaTeX via restoreMath in render/index.ts; Mermaid + Shiki
  // via hydrateMermaid + hydrateCode on the mounted DOM container) so
  // those pipelines never need raw user `style` to make it through.
  // Custom data hooks used across the render pipeline.
  // `data-mermaid-nonce` is the per-render anti-spoof stamp (P1.5) — user
  // pasted literal placeholder divs land here without it and so won't be
  // hydrated; only the renderer's own placeholders survive sanitize with
  // a matching nonce.
  'data-callout', 'data-checked', 'data-line', 'data-mermaid-index',
  'data-mermaid-nonce',
  'data-search', 'data-language',
  // SVG geometry / presentation (used by Mermaid output)
  'viewBox', 'width', 'height', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy',
  'r', 'rx', 'ry', 'd', 'points', 'fill', 'fill-opacity', 'fill-rule',
  'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin',
  'stroke-dasharray', 'stroke-opacity', 'opacity', 'transform',
  'preserveAspectRatio', 'xmlns', 'xmlns:xlink', 'xlink:href',
  'text-anchor', 'dominant-baseline', 'font-size', 'font-family',
  'font-weight', 'font-style', 'marker-end', 'marker-start', 'marker-mid',
  // MathML hooks (KaTeX MathML branch)
  'mathvariant', 'displaystyle', 'scriptlevel',
]

// Allow http(s), mailto, tel for normal links; permit data:image/* but only
// for raster MIME types (png/jpeg/gif/webp) so paste-image data URLs work
// without opening the data: URI smuggling surface for HTML/JS payloads.
// data:image/svg+xml is DELIBERATELY OMITTED — SVG can carry <script> and
// event handlers, and there's no legitimate paste-image flow that needs
// it (browsers paste raster bitmaps). The leading negative branches mirror
// DOMPurify's stock regex shape — anything that doesn't start with a
// scheme-y character (digit, space, hash, slash, etc.) is passed through,
// since DOMPurify runs this matcher across every attribute value
// (including non-URI things like viewBox="0 0 10 10" and fill="red"). The
// bad-scheme blocklist is the bare-string fragment "javascript:" +
// "vbscript:" + "data:" anything-non-allowlisted, which all fail the
// explicit allowlist branch.
const ALLOWED_URI_REGEXP =
  /^(?:(?:https?|mailto|tel):|data:image\/(?:png|jpeg|gif|webp);|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i

// Tags we explicitly never want, even if a future markdown-it plugin emits
// them. DOMPurify defaults already strip <script> + <iframe> + handlers, but
// repeating them here makes the intent grep-visible.
//
// SVG animation tags (animate, animateMotion, animateTransform, set) are
// in DOMPurify's default SVG profile and must be FORBID-listed (not just
// omitted from ADD_TAGS) to actually strip — see audit 2026-05-23 P0 #1.
// They're a known UI-redress / phishing vector (animations can shift UI
// or hide elements off-screen) and not part of V1 feature set.
const FORBID_TAGS = [
  'script', 'iframe', 'object', 'embed', 'link', 'meta', 'base',
  'form', 'style', 'noscript', 'template', 'foreignobject',
  'animate', 'animatemotion', 'animatetransform', 'set',
]

// Attributes we strip on every element regardless of tag. Each entry is
// load-bearing — DOMPurify's html/svg profiles include `style`, `target`,
// `rel`, `role`, `tabindex` by default (so listing them in ADD_ATTR is
// redundant; not listing them in FORBID_ATTR is what would have let them
// through). See audit 2026-05-23 P0 #1: the full-viewport UI-redress
// attack (`<div style="position:fixed;inset:0;…">`) hinged on `style`
// surviving sanitize. Trusted KaTeX / Mermaid / Shiki output is restored
// AFTER sanitize, so none of those pipelines need raw user `style` to
// make it through this function.
//
// `srcdoc` + `sandbox` are iframe-only, but if anything ever sneaks past
// FORBID_TAGS the attribute strip keeps the payload from running.
const FORBID_ATTRS = [
  'srcdoc', 'sandbox', 'formaction', 'xlink:show', 'xlink:actuate',
  'style', 'target', 'rel', 'role', 'tabindex',
]

const CONFIG: Config = {
  // USE_PROFILES turns on DOMPurify's curated HTML + SVG + MathML allowlists,
  // including the SVG attribute matrix (preserveAspectRatio, viewBox, etc.
  // are case-sensitive and tedious to enumerate by hand). ADD_TAGS / ADD_ATTR
  // layer our extras (custom data-callout hooks, post-callout <aside>, etc.)
  // on top of those profiles.
  USE_PROFILES: { html: true, svg: true, svgFilters: true, mathMl: true },
  ADD_TAGS: [
    ...ALLOWED_HTML_TAGS,
    ...ALLOWED_SVG_TAGS,
    ...ALLOWED_MATHML_TAGS,
  ],
  ADD_ATTR: ALLOWED_ATTRS,
  ALLOWED_URI_REGEXP,
  FORBID_TAGS,
  FORBID_ATTR: FORBID_ATTRS,
  // Keep entity-text intact (markdown emoji + HTML-entity escapes round-trip
  // correctly), keep relative URLs, return a string (not a DocumentFragment).
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
}

// Belt-and-braces filter for `data:image/svg+xml` URIs. ALLOWED_URI_REGEXP
// alone is not enough: DOMPurify has a hard-coded fallback at
// `_isValidAttribute` that lets `src` / `href` on tags in its
// DATA_URI_TAGS set (img / audio / video / source / image / track) carry
// any `data:` URI as long as the tag matches — bypassing our regex. SVG
// data URIs can carry inline `<script>` and event handlers, so we strip
// them via a `uponSanitizeAttribute` hook. (DOMPurify's ALLOWED_URI_REGEXP
// stays focused on the other branches; the hook is the dedicated
// `data:image/svg+xml` gate.) See audit 2026-05-23 P0 #1.
let hookInstalled = false
function installHooks(): void {
  if (hookInstalled) return
  hookInstalled = true
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'src' && data.attrName !== 'href' && data.attrName !== 'xlink:href') return
    const v = (data.attrValue || '').trim().toLowerCase()
    if (v.startsWith('data:image/svg+xml')) {
      data.keepAttr = false
      data.attrValue = ''
    }
  })
}

export function sanitize(dirty: string): string {
  installHooks()
  return DOMPurify.sanitize(dirty, CONFIG) as unknown as string
}
